/* eslint-disable react/no-array-index-key */
import {css} from '@emotion/react';
import {useTheme} from '@mui/material';
import {ConfigObjectType, GetStylesFunction, mixins, StatusContainer} from '@wallaby-ui/core';
import {map} from 'lodash';
import * as React from 'react';
import {useIntl} from 'react-intl';

import styles from './NavigationBox.less';
import {NavigationBoxDivider} from './NavigationBoxDivider';
import {NavigationBoxUserSection} from './NavigationBoxUserSection';
import {NavigationExportProcessingInfoContainer} from './NavigationExportProcessingInfoContainer';
import {NavigationHeader} from './NavigationHeader';
import {NavigationItem} from './NavigationItem';
import {ToggleNavigationButton} from './ToggleNavigationButton';
import {UserSectionMenu} from './UserSectionMenu';
import {ValuesToCopyComponent} from '../../ValuesToCopyComponent';
import {NavigationContext, ValuesToCopy} from '../useNavigationContext';

interface OwnProps {
  currentPathName?: string;
  logoName: string;
  logoSignName: string;
  isLoggedIn?: boolean;
  handleLogout: () => void;
  user: string;
  config: ConfigObjectType;
  navigationContext: NavigationContext;
  valuesToCopy: ValuesToCopy;
}

type Props = OwnProps;

export const NavigationBox: React.FC<Props> = (props) => {
  const theme = useTheme();
  const navigation = getStyles({theme});

  const {currentPathName, logoSignName, logoName, handleLogout, isLoggedIn, user, config, navigationContext, valuesToCopy} = props;

  const {
    staticRoutesToShow,
    dynamicRoutesToShow,
    isNavigationOpen,
    isUserMenuOpen,
    setUserMenuOpen,
    removeDynamicItem,
    removeDynamicItemGroup,
  } = navigationContext;
  const intl = useIntl();

  return (
    <div data-name={'navigation-box--box-wrapper'} css={navigation.box}>
      <ToggleNavigationButton navigationContext={navigationContext} />
      <div data-name={'navigation-box--logo-wrapper'} css={navigation.logoWrapper}>
        {isNavigationOpen ? (
          <img key='logoImage' css={navigation.logoImage} data-name={'navigation-box--logo'} src={`/assets/${logoName}`} alt='Logo' />
        ) : (
          <img
            key='logoSignImage'
            css={navigation.logoSignImage}
            data-name={'navigation-box--logo'}
            src={`/assets/${logoSignName}`}
            alt='Logo'
          />
        )}
      </div>
      {isLoggedIn ? (
        <div data-name={'navigation-box--box-content'} css={navigation.boxContent}>
          <NavigationBoxUserSection
            isNavigationOpen={isNavigationOpen}
            isUserMenuOpen={isUserMenuOpen}
            handleOpenUserMenu={() => setUserMenuOpen(true)}
            user={user}
            config={config}
            isTooltipInNavigationMenu
          />
          <UserSectionMenu navigationContext={navigationContext} handleLogout={handleLogout} isNavigationOpen={isNavigationOpen} />
          {map(
            staticRoutesToShow,
            ([group, groupRoutes], i) =>
              groupRoutes.length > 0 && (
                <span data-name={'navigation-box--dlSection'} css={navigation.dlSection} key={i}>
                  {group.id !== 'undefined' && (
                    <NavigationHeader isNavigationOpen={isNavigationOpen} isOpened={isNavigationOpen} routeGroup={group} />
                  )}
                  {groupRoutes.map((route, j) => (
                    <NavigationItem
                      key={j}
                      route={route}
                      isShrank={!isNavigationOpen}
                      currentPathName={currentPathName}
                      navigationContext={navigationContext}
                      isTooltipInNavigationMenu
                      isNavigationOpen={isNavigationOpen}
                      intl={intl}
                    />
                  ))}
                </span>
              )
          )}
          {map(
            dynamicRoutesToShow,
            ([group, groupRoutes], i) =>
              groupRoutes.length > 0 && (
                <span className={styles.dlDynamicGroup} key={i}>
                  <NavigationHeader
                    isNavigationOpen={isNavigationOpen}
                    isOpened={isNavigationOpen}
                    routeGroup={group}
                    removeDynamicItemGroup={removeDynamicItemGroup}
                  />
                  {groupRoutes.map((route, j) => (
                    <NavigationItem
                      key={j}
                      route={route}
                      isShrank={!isNavigationOpen}
                      currentPathName={currentPathName}
                      navigationContext={navigationContext}
                      intl={intl}
                      removeDynamicItem={removeDynamicItem}
                      isTooltipInNavigationMenu
                    />
                  ))}
                </span>
              )
          )}
        </div>
      ) : null}
      <div className={styles.dlFixedColumn}>
        <NavigationBoxDivider isNavigationOpen={isNavigationOpen} />
        <NavigationExportProcessingInfoContainer config={config} isNavigationOpen={isNavigationOpen} />
        {valuesToCopy && <ValuesToCopyComponent valuesToCopy={valuesToCopy} isNavigationOpen={isNavigationOpen} />}
        <StatusContainer applicationLabel={navigationContext.navConfig.appName} isShrank={!isNavigationOpen} />
      </div>
    </div>
  );
};

NavigationBox.displayName = 'NavigationBox';

const getStyles: GetStylesFunction = (options) => ({
  box: css`
    position: relative;
    margin-right: 16px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    order: 1;
    height: 100%;
    background-color: ${options?.theme?.palette.custom.navigationBox as string};
  `,
  boxContent: css`
    overflow: auto;
    ${mixins.dlFlexBoxContainerInContainer};

    scrollbar-width: thin;
    scrollbar-color: var(--dlScrollbarThumbColor) transparent;

    &::-webkit-scrollbar {
      width: var(--dlScrollbarSize);
      height: var(--dlScrollbarSize);
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: var(--dlScrollbarBorderRadius);
      background: var(--dlScrollbarThumbInactiveColor);
    }
    &::-webkit-scrollbar-thumb:hover {
      background: var(--dlScrollbarThumbColor);
    }
  `,
  logoWrapper: css`
    text-align: center;
    overflow: hidden;
    align-self: center;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    justify-content: center;
    height: 56px;
    margin-bottom: 10px;
  `,
  logoImage: css`
    height: 19px;
  `,
  logoSignImage: css``,
  eventHandlerWrapper: css`
    height: 100%;
  `,
  dlSection: css`
    ${mixins.dlFlexBoxContainerInContainer};
    flex: 0 0 auto;
    flex-direction: column;
  `,
});
